import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Brain,
  Briefcase,
  CircleHelp,
  FileStack,
  Mail,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import DashboardLayout from "../components/layout/DashboardLayout";
import RecruiterSidebar from "../components/layout/RecruiterSidebar";

const faqGroups = [
  {
    title: "Getting Started",
    icon: Briefcase,
    items: [
      {
        question: "Why should I create a job before scanning CVs?",
        answer:
          "A job keeps candidates, scan results, rankings, notes, and shortlisting decisions grouped in one place. This makes it easier to review later and avoids mixing applicants from different roles.",
      },
      {
        question: "What should I do after creating a job?",
        answer:
          "Stay on the Jobs page, then choose Add Candidates for that job. After candidates are imported, use Bulk Scan to rank them against the selected job description.",
      },
      {
        question: "Can I import general candidates without linking them to a job?",
        answer:
          "Yes. General candidates can be stored in the candidate pool and added to scans later when you want to compare them against a specific job.",
      },
    ],
  },
  {
    title: "Scanning and Ranking",
    icon: FileStack,
    items: [
      {
        question: "How does RecruitGlance calculate the candidate score?",
        answer:
          "The score combines four layers: semantic job-CV relevance, technical skill coverage, experience evidence, and recruiter criteria with supporting signals such as language, education, certifications, and keywords.",
      },
      {
        question: "What is the experience evidence layer?",
        answer:
          "It checks whether important skills appear in work history, projects, or responsibility context. This helps prevent a CV from ranking too high just because a skill appears once in a keyword list.",
      },
      {
        question: "What is Recruiter Criteria Match?",
        answer:
          "It is the custom scoring layer created from the must-have and preferred skills saved in Job Details. Must-have thresholds strongly influence ranking, while preferred skills add supporting signals.",
      },
      {
        question: "Why did a candidate score lower after I added criteria?",
        answer:
          "A candidate may be generally relevant but still miss a critical must-have skill. If the recruiter criteria says a skill is essential, RecruitGlance reflects that in the score.",
      },
      {
        question: "Why is the score sometimes not perfect even when the CV looks good?",
        answer:
          "The CV may not provide enough direct evidence for required skills, language level, tools, or job-specific experience. RecruitGlance ranks based on evidence found in the CV and job description.",
      },
    ],
  },
  {
    title: "AI Enrichment",
    icon: Brain,
    items: [
      {
        question: "Why do only the top five candidates get deeper AI explanation automatically?",
        answer:
          "The top five are enriched first to keep the scan fast and cost-efficient. Recruiters get richer reasoning where it matters most, while the rest still receive structured score and skill signals.",
      },
      {
        question: "What does AI enrichment include?",
        answer:
          "AI enrichment can include summary, matching skills, gaps, skill evidence score, qualification signals, language match, and interview questions based on candidate gaps.",
      },
      {
        question: "Can AI results be wrong?",
        answer:
          "Yes. AI results should be treated as decision support, not final hiring decisions. Recruiters should review the CV, evidence, and interview outcomes before making decisions.",
      },
      {
        question: "Why do some candidates not show an AI summary immediately?",
        answer:
          "Only selected or top-ranked candidates are enriched automatically. Others can remain as standard ranked results depending on the workflow and plan limits.",
      },
    ],
  },
  {
    title: "Candidate Import",
    icon: Mail,
    items: [
      {
        question: "Why did Gmail import a wrong PDF?",
        answer:
          "Email attachments can include bills, forms, or unrelated PDFs. Email import includes recruiter review so incorrect files can be rejected before being saved.",
      },
      {
        question: "How does job-based email import avoid unrelated CVs?",
        answer:
          "The import flow filters emails using the selected job context, such as job title and related terms. Recruiters should still review the pending imports before approval.",
      },
      {
        question: "What happens after I import candidates for a job?",
        answer:
          "The imported candidates appear under that job and can be scanned from Bulk Scan. Newly imported candidates can be preselected when you continue directly to scanning.",
      },
      {
        question: "Why does AI Talent Search preparation continue after candidates are ready for scan?",
        answer:
          "Scan readiness and AI Talent Search preparation are separate. Candidates can be scanned quickly while RecruitGlance prepares them for future pool searches in Background Activity.",
      },
    ],
  },
  {
    title: "Duplicates and History",
    icon: FileStack,
    items: [
      {
        question: "Why did RecruitGlance show a duplicate result?",
        answer:
          "The same CV may already have been scanned for the same job. Reusing previous results avoids unnecessary processing unless you choose to refresh the result.",
      },
      {
        question: "When should I rescan candidates?",
        answer:
          "Rescan when the job description, scoring criteria, or matching logic changed. If nothing changed, the previous result is usually still valid.",
      },
      {
        question: "Where can I review previous scan results?",
        answer:
          "Use the Rankings page to select a job and review candidate ranking history, shortlist status, notes, and AI-enriched results.",
      },
      {
        question: "Can I send shortlist or rejection emails from previous scan results?",
        answer:
          "Yes. Open Rankings or Scan History, mark candidates as shortlisted or rejected, then use Email Shortlisted or Email Rejected. Already emailed candidates are grouped separately and excluded from the next popup unless unlocked.",
      },
    ],
  },
  {
    title: "Candidate Emails",
    icon: Mail,
    items: [
      {
        question: "Where do I configure candidate email templates?",
        answer:
          "Open Profile, then App Configuration, then Candidate Email Settings. You can set the recruiter sender name, sender email, shortlisted email template, and rejected email template.",
      },
      {
        question: "Which template variables can I use?",
        answer:
          "Templates can use variables such as {candidate_name}, {job_name}, {recruiter_name}, and {company_name}. RecruitGlance replaces these values when preparing the email.",
      },
      {
        question: "Why should candidate emails come from the recruiter?",
        answer:
          "RecruitGlance supports the workflow, but hiring communication should come from the recruiter or company that owns the candidate relationship.",
      },
    ],
  },
  {
    title: "Plans and Access",
    icon: ShieldCheck,
    items: [
      {
        question: "What happens when scans run out?",
        answer:
          "Scanning actions are blocked until the quota refreshes or the account is upgraded. Feature pages can still be previewed so users understand what is available.",
      },
      {
        question: "Why are some menu items locked?",
        answer:
          "Some features are available only on specific plans. Locked items show what the feature does and guide users toward the right plan.",
      },
      {
        question: "Why is Single CV Scan hidden for paid users?",
        answer:
          "The main workflow for paid users is job-based candidate import and Bulk Scan, which provides better history, ranking, and recruiter decision support.",
      },
    ],
  },
];

export default function FAQ() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const leftPanel = <RecruiterSidebar activeItem="faq" user={user} />;

  const mainContent = (
    <div className="space-y-6">
      <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
              <Sparkles size={14} />
              Recruiter FAQ
            </span>
            <h2 className="mt-3 text-2xl font-semibold text-emerald-950">
              Clear answers for common screening questions
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-emerald-800">
              Use this page when a recruiter asks how scoring, AI enrichment, candidate import,
              duplicates, or plan limits work in RecruitGlance.
            </p>
          </div>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        {faqGroups.map((group) => {
          const Icon = group.icon;

          return (
            <article
              key={group.title}
              className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
            >
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-950 text-white">
                  <Icon size={19} />
                </span>
                <h3 className="text-lg font-semibold text-slate-950">{group.title}</h3>
              </div>

              <div className="divide-y divide-slate-100">
                {group.items.map((item) => (
                  <details key={item.question} className="group py-3">
                    <summary className="flex cursor-pointer list-none items-start gap-2 text-sm font-semibold text-slate-800">
                      <CircleHelp size={16} className="mt-0.5 flex-shrink-0 text-emerald-600" />
                      <span>{item.question}</span>
                    </summary>
                    <p className="mt-2 pl-6 text-sm leading-6 text-slate-600">{item.answer}</p>
                  </details>
                ))}
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );

  return (
    <DashboardLayout
      user={user}
      eyebrow="Frequently asked questions"
      title="FAQ"
      subtitle="Answers recruiters may ask before trusting the screening workflow."
      leftPanel={leftPanel}
      mainContent={mainContent}
    />
  );
}
